import React, {useMemo} from "react"
import {useParams, Link} from "react-router-dom"
import {useQuery} from "react-query"
import moment from "moment"
import useAuth from "auth/useAuth"
import "./ReportReviewPage.css"


const SEVERITY_ICONS = {
    error: "fas fa-times-circle",
    warning: "fas fa-exclamation-triangle",
    suggestion: "fas fa-info-circle"
}

const FIELD_LABELS = {
    "subject": "Povređeni",
    "subject.given_name": "Ime",
    "subject.family_name": "Prezime",
    "subject.birth_date": "Datum rođenja",
    "subject.birth_place": "Mesto rođenja",
    "subject.address": "Adresa",
    "location": "Lokacija",
    "location.coordinates": "Koordinate",
    "location.description": "Opis lokacije",
    "location_description": "Opis lokacije",
    "description": "Opis akcije",
    "treatment": "Pomoć",
    "treatment_desc": "Opis pomoći",
    "events": "Događaji",
    "injuries": "Povrede",
    "weather": "Vreme",
    "subject_category": "Kategorija",
    "ski_pass": "Ski-pass",
    "accommodation": "Smeštaj",
    "receiver": "Preuzimanje",
    "belongings": "Oprema",
    "transport": "Prevoz"
}


export default function ReportReviewPage() {

    const {auth, API} = useAuth()
    const {area, date} = useParams()

    const {data, isLoading, isError, error} = useQuery(
        ["report-review", area, date],
        () => auth(API.reportsAreaReviewDateGet, {area, date}).then(r => r.data),
        {
            staleTime: 60000,
            retry: 1,
            refetchInterval: (d) => d?.llm_status === "processing" ? 4000 : false
        }
    )

    const counts = useMemo(() => {
        if (!data?.rescuer_reviews) return {errors: 0, warnings: 0, suggestions: 0}
        let errors = 0, warnings = 0, suggestions = 0
        for (const rescuer of data.rescuer_reviews) {
            for (const rescue of rescuer.rescue_reviews) {
                for (const f of rescue.findings) {
                    if (f.severity === "error") errors++
                    else if (f.severity === "warning") warnings++
                    else suggestions++
                }
            }
        }
        return {errors, warnings, suggestions}
    }, [data])

    const total = counts.errors + counts.warnings + counts.suggestions

    return <div className="container-fluid shadow white" id="report-review">
        <section className="white page-head review-header">
            <h3>Pregled listi</h3>
            <div>
                <Link to={encodeURI(`/report/${area}/${date}`)}>
                    <i className="fas fa-arrow-left"/> Nazad
                </Link>
            </div>
            <span>{area} &middot; {moment(date).format("Do MMMM YYYY.")}</span>
        </section>

        {isLoading && <div className="review-loading">
            <div className="spinner-border text-primary" role="status"/>
            <p>Analiza u toku...</p>
        </div>}

        {isError && <div className="review-error">
            <i className="fas fa-exclamation-triangle"/> {error?.response?.data || "Greška pri učitavanju"}
        </div>}

        {data?.rescuer_reviews && <>
            <section className="white review-summary">
                {total === 0
                    ? <span className="all-good"><i className="fas fa-check-circle"/> Sve izgleda u redu</span>
                    : <>
                        {counts.errors > 0 && <span className="count errors">
                            <i className="fas fa-times-circle"/> {counts.errors} {counts.errors === 1 ? "greška" : "grešaka"}
                        </span>}
                        {counts.warnings > 0 && <span className="count warnings">
                            <i className="fas fa-exclamation-triangle"/> {counts.warnings} {counts.warnings === 1 ? "upozorenje" : "upozorenja"}
                        </span>}
                        {counts.suggestions > 0 && <span className="count suggestions">
                            <i className="fas fa-info-circle"/> {counts.suggestions} {counts.suggestions === 1 ? "predlog" : "predloga"}
                        </span>}
                    </>
                }
                {data?.llm_status === "processing" && <span className="llm-processing">
                    <span className="spinner-border spinner-border-sm"/> AI analiza u toku...
                </span>}
            </section>

            {data.rescuer_reviews.map(rescuer => <div key={rescuer.username} className="rescuer-section">
                <div className="rescuer-section-header">
                    <span className="rescuer-name">{rescuer.given_name} {rescuer.family_name}</span>
                </div>

                {rescuer.rescue_reviews.map(rescue => <div key={rescue.rescue_id} className="rescue-card">
                    <div className="rescue-card-header">
                        <Link to={`/rescue/${rescue.rescue_id}`}>#{rescue.rescue_short_id}</Link>
                        <span className="subject-name">{rescue.subject_name}</span>
                        {rescue.findings.length === 0 && <span className="no-findings">
                            <i className="fas fa-check"/> OK
                        </span>}
                    </div>

                    {rescue.findings.map((finding, i) => <div key={i} className="finding">
                        <span className={`finding-icon ${finding.severity}`}>
                            <i className={SEVERITY_ICONS[finding.severity]}/>
                        </span>
                        <span className="finding-field">{FIELD_LABELS[finding.field] || finding.field}</span>
                        <span className="finding-message">
                            {finding.message}
                            {finding.source === "llm" && <span className="llm-badge" title="AI analiza"><i className="fas fa-robot"/></span>}
                        </span>
                    </div>)}
                </div>)}
            </div>)}
        </>}
    </div>
}
